import { z } from 'zod';
import type {
  activitiesActivityFlowProjectPathObj,
  budgetAndExecutionPathObj,
  contractorPaymentIdProjectPathObj,
  legalBasesProjectPathObj,
  otherIndicatedItemAndResponseReasonProjectPathObj,
  paymentAmountBreakdownPathObj,
  paymentGroupProjectPathObj,
  planProjectPathObj,
  policyProjectPathObj,
  projectPathObj,
  rootActivityPathObj,
  subsidyRateAndSoOnProjectPathObj,
  summaryAndResponseProcessProjectPathObj,
} from '../models/pinpoint-path';

//// 基本情報タブ

export const RsBasicInformationScenePinpointFieldEnum = z.enum([
  'rs.recurring-stats-table',
  'rs.recurring-stats-graph',
  'rs.snapshot',
  'rs.organization-in-charge',
  'rs.people-in-charge',
  'rs.business-manager',
  'rs.other-organization-in-charge',
  'rs.related-organizations',
  'rs.project-number',
  'rs.project-group',
  'rs.sheet-type',
  'rs.fiscal-year',
  'rs.project-category',
  'rs.period',
  'rs.policy-title',
  'rs.policy',
  'rs.related-projects',
  'rs.main-cost',
  'rs.project-purpose',
  'rs.project-task',
  'rs.overview',
  'rs.overview-url',
  'rs.legal-bases-title',
  'rs.legal-bases',
  'rs.plan-title',
  'rs.plan',
  'rs.implementation-method',
  'rs.subsidy-rate-and-so-on-title',
  'rs.subsidy-rate-and-so-on',
  'rs.old-project-number',
  'rs.project-order-number',
  'rs.remark',
]);

//// 予算執行タブ
export const RsDetailedBreakdownScenePinpointFieldEnum = z.enum([
  'rs.budget-and-execution-title',
  'rs.budget-and-execution',
  'rs.increase-decrease-main-reason',
  'rs.other-special-notes',
]);

// 「アクティビティ／活動・成果目標等」タブ
export const RsActivitiesScenePinpointFieldEnum = z.enum([
  'rs.cabinet-decision-with-defined-indicators-name',
  'rs.cabinet-decision-with-defined-indicators-url',
  'rs.cabinet-decision-with-defined-indicators-relevant-part',
  'rs.activity-title',
]);

// 活動・成果目標等のつながりタブ
export const RsActivityFlowScenePinpointFieldEnum = z.enum([
  'rs.activity',
  'rs.activities-and-outcomes-title',
  'rs.indicator-title',
  'rs.indicator-description',
  'rs.activity-and-result-target',
  'rs.achievement-for-qualitative-outcome',
  'rs.indicator-setting-reason',
  'rs.reason-for-setting-qualitative-outcome',
  'rs.reason-why-outcomes-cannot-be-multiple',
  'rs.connection-to-next-outcome',
]);

// 点検評価タブ
export const RsInspectionEvaluationScenePinpointFieldEnum = z.enum([
  'rs.inspection-result',
  'rs.evaluation',
  'rs.direction-of-improvement',
  'rs.inspection-target',
  'rs.last-implemented-fiscal-year',
  'rs.reason-of-target',
  'rs.opinion',
  'rs.result-of-publishing',
  'rs.process-and-opinion-by-administrative-review-team-opinion',
  'rs.process-and-opinion-by-administrative-review-team-details',
  'rs.improvement-and-reflection-status',
  'rs.reflected-amount',
  'rs.reflection-status-based-on-opinion-details',
  'rs.summary-of-publishing-title',
  'rs.summary-of-publishing',
  'rs.others-indicated-item-title',
  'rs.others-indicated-item',
]);

// 支出先タブ
export const RsPaymentScenePinpointFieldEnum = z.enum([
  'rs.payment-title',
  'rs.payment-group',
  'rs.payment-contractor-title',
  'rs.payment-contractor-row',
  'rs.payment-amount-breakdown-title',
  'rs.payment-amount-breakdown-row',
]);

export const RsRelatedMaterialScenePinpointFieldEnum = z.enum([
  'rs.related-material-image',
  'rs.related-material-remark',
]);

export const RsPinpointFieldEnum = z.enum([
  ...RsBasicInformationScenePinpointFieldEnum.options,
  ...RsDetailedBreakdownScenePinpointFieldEnum.options,
  ...RsRelatedMaterialScenePinpointFieldEnum.options,
  ...RsPaymentScenePinpointFieldEnum.options,
  ...RsInspectionEvaluationScenePinpointFieldEnum.options,
  ...RsActivityFlowScenePinpointFieldEnum.options,
  ...RsActivitiesScenePinpointFieldEnum.options,
]);

/// ////////////////////////////////////////////
/// pinpointの値の組み合わせを明示的に定義する
/// ////////////////////////////////////////////

export const RsPinpointMultipleFieldEnum = z.enum([
  RsPinpointFieldEnum.Enum['rs.policy'],
  RsPinpointFieldEnum.Enum['rs.legal-bases'],
  RsPinpointFieldEnum.Enum['rs.plan'],
  RsPinpointFieldEnum.Enum['rs.subsidy-rate-and-so-on'],
  RsDetailedBreakdownScenePinpointFieldEnum.Enum['rs.budget-and-execution'],
  RsPaymentScenePinpointFieldEnum.Enum['rs.payment-group'],
  RsPaymentScenePinpointFieldEnum.Enum['rs.payment-contractor-row'],
  RsInspectionEvaluationScenePinpointFieldEnum.Enum['rs.summary-of-publishing'],
  RsInspectionEvaluationScenePinpointFieldEnum.Enum['rs.others-indicated-item'],
  ...RsActivityFlowScenePinpointFieldEnum.options.filter(
    (field) => field !== 'rs.activities-and-outcomes-title',
  ),
  RsPaymentScenePinpointFieldEnum.Enum['rs.payment-amount-breakdown-row'],
]);

export type RsActivitiesAndOutcomesTitle = {
  path: z.infer<typeof projectPathObj>;
  field: (typeof RsActivityFlowScenePinpointFieldEnum.enum)['rs.activities-and-outcomes-title'];
};

type ProjectPinpointMappingType =
  | {
      path: z.infer<typeof projectPathObj>;
      field: Exclude<
        | z.infer<typeof RsBasicInformationScenePinpointFieldEnum>
        | z.infer<typeof RsDetailedBreakdownScenePinpointFieldEnum>
        | z.infer<typeof RsRelatedMaterialScenePinpointFieldEnum>
        | z.infer<typeof RsInspectionEvaluationScenePinpointFieldEnum>
        | z.infer<typeof RsActivitiesScenePinpointFieldEnum>
        | z.infer<typeof RsPaymentScenePinpointFieldEnum>,
        z.infer<typeof RsPinpointMultipleFieldEnum>
      >;
    }
  | RsActivitiesAndOutcomesTitle;

export type RsActivityFlowPinpointMappingType = {
  path: z.infer<typeof activitiesActivityFlowProjectPathObj>;
  field: Exclude<
    z.infer<typeof RsActivityFlowScenePinpointFieldEnum>,
    'rs.activity' | 'rs.activities-and-outcomes-title'
  >;
};

type RsPinpointMultipleFieldEnumFieldEnumType =
  typeof RsPinpointMultipleFieldEnum.Enum;

export type RsMultiplePinpointMappingType =
  | {
      path: z.infer<typeof policyProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.policy'];
    }
  | {
      path: z.infer<typeof legalBasesProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.legal-bases'];
    }
  | {
      path: z.infer<typeof planProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.plan'];
    }
  | {
      path: z.infer<typeof subsidyRateAndSoOnProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.subsidy-rate-and-so-on'];
    }
  | {
      path: z.infer<typeof budgetAndExecutionPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.budget-and-execution'];
    }
  | {
      path: z.infer<typeof paymentGroupProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.payment-group'];
    }
  | {
      path: z.infer<typeof contractorPaymentIdProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.payment-contractor-row'];
    }
  | {
      path: z.infer<typeof summaryAndResponseProcessProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.summary-of-publishing'];
    }
  | {
      path: z.infer<typeof otherIndicatedItemAndResponseReasonProjectPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.others-indicated-item'];
    }
  | {
      path: z.infer<typeof rootActivityPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.activity'];
    }
  | {
      path: z.infer<typeof paymentAmountBreakdownPathObj>;
      field: RsPinpointMultipleFieldEnumFieldEnumType['rs.payment-amount-breakdown-row'];
    }
  | RsActivityFlowPinpointMappingType;

export type RsPinpointMappingType =
  | ProjectPinpointMappingType
  | RsMultiplePinpointMappingType;
